import { useCallback } from 'react'
import * as React from 'react'
import { toBaseFareClass, type FlightsClassTypes, type FareClassWithReward } from 'constants/flight'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import MaldivesCharterCalendarBanner from 'components/Flights/CharterBundle/MaldivesCharterCalendarBanner'
import { MALDIVES_CHARTER_ROUTE } from 'api/lib/flightDealMap'
import CharterBundleCalendarActions from 'components/OfferPage/Filters/CharterFlight/CharterBundleCalendarActions'
import MaldivesDirectSearchFlightCalendar from './MaldivesDirectCalendar/MaldivesDirectSearchFlightCalendar'
import MaldivesDirectSearchFlightCalendarMobile from './MaldivesDirectCalendar/MaldivesDirectSearchFlightCalendarMobile'
import { useMaldivesDirectCalendarPricing } from './hooks/useMaldivesDirectCalendarPricing'
import type { DateRange } from './hooks/types'
import { MaldivesCharterCalendarModalDesktop, MaldivesCharterCalendarModalMobile } from './MaldivesDirectCalendar/MaldivesCharterCalendarModalLayout'

const MALDIVES_DIRECT_SEARCH_CALENDAR_MODAL_TEST_ID = 'maldives-direct-search-calendar-modal'

interface CalendarSelectionOption {
  departDate: string
  returnDate: string
  checkIn: string
  checkOut: string
  fareClass?: FareClassWithReward
}

type CharterBundleCalendarActionsProps = React.ComponentProps<typeof CharterBundleCalendarActions>

interface MaldivesDirectSearchCalendarModalProps {
  isOpen: boolean
  title: string | React.ReactElement
  onClose: () => void
  onDateSelect: (option: CalendarSelectionOption) => void
  nights: number
  rooms: Array<App.Occupants>
  luxPlusEnabled: boolean
  dateRangesByMonth: Record<string, Array<DateRange>>
  selectedMonth: string
  onMonthChange: (month: string) => void
  fareClassForSearch: FlightsClassTypes
  selectedFareClass?: FareClassWithReward | null
  priceMode: 'total' | 'perPerson'
  onPriceModeChange: (priceMode: 'total' | 'perPerson') => void
  selectedOptionId: string
  canStreamHotelOffers: boolean
  onDurationChange: (nights: number) => void
  durationOptions: CharterBundleCalendarActionsProps['durationOptions']
  onLuxPlusToggle: (enabled: boolean) => void
  onFareClassChange: (fareClass: FareClassWithReward | null) => void
  availableFareClasses: ReadonlyArray<FareClassWithReward>
  rewardSeatsOnly: boolean
  onRewardSeatsOnlyChange: (enabled: boolean) => void
  infiniteMemberOnly: boolean
  onInfiniteMemberOnlyChange: (enabled: boolean) => void
  /**
   * Forwarded to /flightsearch via useMaldivesDirectCalendarPricing so calendar
   * prices include feederEstimatedPrice and share a Redux cache key with
   * feeder-aware callers.
   */
  feederOriginAirportCode?: string
  cabinFilter?: FlightsClassTypes | null
  onCabinFilterChange?: (cabin: FlightsClassTypes | null) => void
}

export default function MaldivesDirectSearchCalendarModal(props: MaldivesDirectSearchCalendarModalProps) {
  const {
    isOpen,
    title,
    onClose,
    onDateSelect,
    nights,
    rooms,
    luxPlusEnabled,
    dateRangesByMonth,
    selectedMonth,
    onMonthChange,
    fareClassForSearch,
    selectedFareClass,
    priceMode,
    onPriceModeChange,
    selectedOptionId,
    canStreamHotelOffers,
    onDurationChange,
    durationOptions,
    onLuxPlusToggle,
    onFareClassChange,
    availableFareClasses,
    feederOriginAirportCode,
    rewardSeatsOnly,
    onRewardSeatsOnlyChange,
    infiniteMemberOnly,
    onInfiniteMemberOnlyChange,
    cabinFilter,
    onCabinFilterChange,
  } = props
  const isDesktop = useScreenSizeUp('desktop')
  const { calendarPricingByDate, hotelPriceByCabinAndDate } = useMaldivesDirectCalendarPricing({
    rooms,
    fareClass: fareClassForSearch,
    selectedFareClass: selectedFareClass ? toBaseFareClass(selectedFareClass) : undefined,
    nights,
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    dateRangesByMonth,
    disabled: !canStreamHotelOffers || !isOpen,
    feederOriginAirportCode,
  })

  // Wrap onDateSelect to attach the selected fare class so the URL updates with both
  // the date range and the cabin when a calendar item is selected.
  // If the option already carries a fareClass (e.g. desktop table per-column select), keep it;
  // otherwise fall back to the modal-level dropdown selection.
  const handleDateSelectWithFareClass = useCallback((option: CalendarSelectionOption) => {
    onDateSelect({
      ...option,
      fareClass: option.fareClass ?? selectedFareClass ?? undefined,
    })
  }, [onDateSelect, selectedFareClass])

  if (!isOpen) {
    return null
  }

  if (isDesktop) {
    return (
      <MaldivesCharterCalendarModalDesktop
        isOpen={isOpen}
        onClose={onClose}
        title={title}
        $testId={MALDIVES_DIRECT_SEARCH_CALENDAR_MODAL_TEST_ID}
        headerExtension={
          <CharterBundleCalendarActions
            nights={nights}
            onDurationChange={onDurationChange}
            durationOptions={durationOptions}
            luxPlusEnabled={luxPlusEnabled}
            onLuxPlusToggle={onLuxPlusToggle}
            cabinFilter={cabinFilter}
            onCabinFilterChange={onCabinFilterChange}
            availableFareClasses={availableFareClasses}
            rewardSeatsOnly={rewardSeatsOnly}
            onRewardSeatsOnlyChange={onRewardSeatsOnlyChange}
            infiniteMemberOnly={infiniteMemberOnly}
            onInfiniteMemberOnlyChange={onInfiniteMemberOnlyChange}
          />
        }
        size="max"
        flushListItems
        data-testid={MALDIVES_DIRECT_SEARCH_CALENDAR_MODAL_TEST_ID}
      >
        <MaldivesCharterCalendarBanner />
        <MaldivesDirectSearchFlightCalendar
          onDateSelect={handleDateSelectWithFareClass}
          nights={nights}
          occupancies={rooms}
          luxPlusEnabled={luxPlusEnabled}
          dateRangesByMonth={dateRangesByMonth}
          selectedMonth={selectedMonth}
          onMonthChange={onMonthChange}
          fareClass={fareClassForSearch}
          selectedFareClass={selectedFareClass ?? undefined}
          priceMode={priceMode}
          onPriceModeChange={onPriceModeChange}
          selectedOptionId={selectedOptionId}
          mode="search"
          itemBundleDataByDate={calendarPricingByDate}
          hotelPriceByCabinAndDate={hotelPriceByCabinAndDate}
          rewardSeatsOnly={rewardSeatsOnly}
          infiniteMemberOnly={infiniteMemberOnly}
          onInfiniteMemberOnlyChange={onInfiniteMemberOnlyChange}
          feederOriginAirportCode={feederOriginAirportCode}
          cabinFilter={cabinFilter ?? undefined}
        />
      </MaldivesCharterCalendarModalDesktop>
    )
  }

  return (
    <MaldivesCharterCalendarModalMobile
      height="full"
      title={title}
      isOpen={isOpen}
      onClose={onClose}
      isPrimaryActionHidden
      flushListItems
    >
      <MaldivesDirectSearchFlightCalendarMobile
        onDateSelect={onDateSelect}
        nights={nights}
        occupancies={rooms}
        luxPlusEnabled={luxPlusEnabled}
        dateRangesByMonth={dateRangesByMonth}
        selectedMonth={selectedMonth}
        onMonthChange={onMonthChange}
        fareClass={fareClassForSearch}
        selectedFareClass={selectedFareClass}
        cabinFilter={cabinFilter ?? undefined}
        onFareClassChange={onFareClassChange}
        priceMode={priceMode}
        selectedOptionId={selectedOptionId}
        mode="search"
        itemBundleDataByDate={calendarPricingByDate}
        feederOriginAirportCode={feederOriginAirportCode}
        rewardSeatsOnly={rewardSeatsOnly}
        infiniteMemberOnly={infiniteMemberOnly}
        hotelPriceByCabinAndDate={hotelPriceByCabinAndDate}
        headerActions={
          <CharterBundleCalendarActions
            nights={nights}
            onDurationChange={onDurationChange}
            durationOptions={durationOptions}
            luxPlusEnabled={luxPlusEnabled}
            onLuxPlusToggle={onLuxPlusToggle}
            cabinFilter={cabinFilter}
            onCabinFilterChange={onCabinFilterChange}
            availableFareClasses={availableFareClasses}
            rewardSeatsOnly={rewardSeatsOnly}
            onRewardSeatsOnlyChange={onRewardSeatsOnlyChange}
            infiniteMemberOnly={infiniteMemberOnly}
            onInfiniteMemberOnlyChange={onInfiniteMemberOnlyChange}
            priceMode={priceMode}
            onPriceModeChange={onPriceModeChange}
          />
        }
        stickyHeaderBanner={<MaldivesCharterCalendarBanner flush />}
      />
    </MaldivesCharterCalendarModalMobile>
  )
}
